// src/pages/agent/Buyers.js
import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, Field, Input, BORDER, MID, LIGHT, BURG_DK, EmptyState } from '../../components/agent/UI';

export default function Buyers() {
  const { profile } = useAgentAuth();
  const [buyers, setBuyers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    if (!profile?.uid) return;
    const q = query(collection(db, 'agents', profile.uid, 'buyers'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setBuyers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [profile?.uid]);

  const remove = async (id) => {
    if (!window.confirm('Remove this buyer from your list?')) return;
    await deleteDoc(doc(db, 'agents', profile.uid, 'buyers', id));
  };

  return (
    <>
      <PageHeader
        eyebrow="Cash buyers"
        title="Buyers list"
        sub="The investors and cash buyers you assign contracts to."
        right={<Btn variant="gold" onClick={() => setShowAdd(true)}>+ Add buyer</Btn>}
      />

      <Card padded={false}>
        {buyers.length === 0 ? (
          <EmptyState
            title="No buyers yet"
            sub="Add cash buyers manually or import from your Facebook leads."
            action={<Btn variant="gold" onClick={() => setShowAdd(true)}>+ Add a buyer</Btn>}
          />
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Phone</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Markets</th>
                <th style={s.th}>Price range</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {buyers.map(b => (
                <tr key={b.id} style={s.tr}>
                  <td style={s.td}><div style={{ fontWeight: 500, color: BURG_DK }}>{b.name}</div></td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{b.phone || '—'}</td>
                  <td style={s.td}>{b.email || '—'}</td>
                  <td style={s.td}>{b.markets || '—'}</td>
                  <td style={s.td}>{b.priceRange || '—'}</td>
                  <td style={s.td}><button onClick={() => remove(b.id)} style={s.removeBtn}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showAdd && <AddBuyer agentUid={profile.uid} onClose={() => setShowAdd(false)} />}
    </>
  );
}

function AddBuyer({ agentUid, onClose }) {
  const [b, setB] = useState({ name: '', phone: '', email: '', markets: '', priceRange: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setB(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!b.name.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'agents', agentUid, 'buyers'), {
        ...b, createdAt: serverTimestamp()
      });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={m.modal} onClick={e => e.stopPropagation()}>
        <h2 style={m.title}>Add buyer</h2>
        <form onSubmit={submit}>
          <Field label="Name" required><Input autoFocus value={b.name} onChange={e => set('name', e.target.value)} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Phone"><Input value={b.phone} onChange={e => set('phone', e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={b.email} onChange={e => set('email', e.target.value)} /></Field>
          </div>
          <Field label="Markets / areas" hint="e.g. Atlanta, Decatur, College Park"><Input value={b.markets} onChange={e => set('markets', e.target.value)} /></Field>
          <Field label="Price range"><Input value={b.priceRange} onChange={e => set('priceRange', e.target.value)} placeholder="$150K – $300K" /></Field>
          <div style={m.actions}>
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save buyer'}</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

const s = {
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 18px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}` },
  tr: {},
  td: { padding: '14px 18px', fontSize: 13, borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C' },
  removeBtn: { background: 'transparent', border: 'none', color: '#991B1B', fontSize: 11, cursor: 'pointer', padding: 0 }
};
const m = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px' },
  modal: { background: '#fff', borderRadius: 8, maxWidth: 480, width: '100%', padding: '28px 28px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 500, color: BURG_DK, margin: '0 0 18px' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }
};
